import React from 'react';
import { Flame } from 'lucide-react';

/**
 * FoundrScore Readiness Badge - Nando's spice-level inspired design
 * RED = Lemon & Herb (Needs Work) - 1 flame
 * AMBER = Medium (Promising) - 2 flames  
 * GREEN = Extra Hot (Test-Ready) - 3 flames
 */
export default function ReadinessBadge({ level, percentage, showLabel = true, size = 'lg' }) {
  const config = {
    RED: {
      flames: 1,
      label: 'Needs Work',
      sublabel: 'Fix the basics first',
      bgGradient: 'from-red-500 to-red-600',
      bgLight: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-700',
      flameColor: 'text-red-500',
      inactiveFlame: 'text-red-200',
      description: 'Core basics unproven — fix problem and customer first'
    },
    AMBER: {
      flames: 2,
      label: 'Promising',
      sublabel: 'Close the gaps',
      bgGradient: 'from-amber-500 to-orange-500',
      bgLight: 'bg-amber-50',
      borderColor: 'border-amber-200',
      textColor: 'text-amber-700',
      flameColor: 'text-amber-500',
      inactiveFlame: 'text-amber-200',
      description: 'Promising but important gaps — Full Assessment recommended'
    },
    GREEN: {
      flames: 3,
      label: 'Test-Ready',
      sublabel: 'Strong foundations',
      bgGradient: 'from-emerald-500 to-green-500',
      bgLight: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      textColor: 'text-emerald-700',
      flameColor: 'text-emerald-500',
      inactiveFlame: 'text-emerald-200',
      description: 'Strong foundations — move to Full Assessment'
    }
  };

  const current = config[level] || config.AMBER;
  const sizeClasses = {
    sm: { badge: 'w-32', flame: 'w-5 h-5', text: 'text-sm', percent: 'text-2xl' },
    md: { badge: 'w-48', flame: 'w-6 h-6', text: 'text-base', percent: 'text-3xl' },
    lg: { badge: 'w-64', flame: 'w-8 h-8', text: 'text-lg', percent: 'text-4xl' }
  };
  const sizes = sizeClasses[size] || sizeClasses.lg;

  return (
    <div className={`${sizes.badge} mx-auto`} data-testid="readiness-badge">
      {/* Main Badge Container */}
      <div className={`relative rounded-2xl overflow-hidden ${current.bgLight} ${current.borderColor} border-2 shadow-lg`}>
        {/* Top gradient bar */}
        <div className={`h-2 bg-gradient-to-r ${current.bgGradient}`} />
        
        {/* Badge content */}
        <div className="p-6 text-center">
          {/* Flame indicators - Nando's style */}
          <div className="flex justify-center gap-1 mb-3" data-testid="flame-indicators">
            {[1, 2, 3].map((flameNum) => (
              <Flame
                key={flameNum}
                className={`${sizes.flame} transition-all duration-300 ${
                  flameNum <= current.flames 
                    ? `${current.flameColor} drop-shadow-md` 
                    : current.inactiveFlame
                } ${flameNum <= current.flames ? 'animate-pulse' : ''}`}
                fill={flameNum <= current.flames ? 'currentColor' : 'none'}
                data-testid={`flame-${flameNum}`}
              />
            ))}
          </div>

          {/* Percentage score */}
          {percentage !== undefined && (
            <div className={`${sizes.percent} font-bold ${current.textColor} mb-1`} data-testid="score-percentage">
              {Math.round(percentage)}%
            </div>
          )}

          {/* Level label */}
          {showLabel && (
            <>
              <div className={`${sizes.text} font-semibold ${current.textColor} uppercase tracking-wide`} data-testid="level-label">
                {current.label}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {current.sublabel}
              </div>
            </>
          )}
        </div>

        {/* Bottom indicator strip */}
        <div className="flex">
          <div className={`flex-1 h-1 ${level === 'RED' ? `bg-gradient-to-r ${current.bgGradient}` : 'bg-gray-200'}`} />
          <div className={`flex-1 h-1 ${level === 'AMBER' || level === 'GREEN' ? `bg-gradient-to-r ${config.AMBER.bgGradient}` : 'bg-gray-200'}`} />
          <div className={`flex-1 h-1 ${level === 'GREEN' ? `bg-gradient-to-r ${current.bgGradient}` : 'bg-gray-200'}`} />
        </div>
      </div>

      {/* Description below badge */}
      <p className="text-center text-sm text-muted-foreground mt-4 px-2" data-testid="level-description">
        {current.description}
      </p>
    </div>
  );
}

/**
 * Compact inline badge for lists/cards
 */
export function ReadinessBadgeInline({ level }) {
  const config = {
    RED: { bg: 'bg-red-100', text: 'text-red-700', label: 'RED' },
    AMBER: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'AMBER' },
    GREEN: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'GREEN' }
  };
  const current = config[level] || config.AMBER;

  return (
    <span 
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${current.bg} ${current.text}`}
      data-testid="readiness-badge-inline"
    >
      <Flame className="w-3 h-3" fill="currentColor" />
      {current.label}
    </span>
  );
}
